/**
 * Juniper Query API - New API Integration
 * Connects to the optimized juniper-query endpoints with field selection,
 * live data support, and progressive loading capabilities.
 */

import { api } from '@xtnd/web/shared/api';
import type {
  TransactionQueryParams,
  Transaction,
  TransactionListResponse,
  FIELD_PRESETS,
  FieldPreset,
} from '../types';

// Re-export types for backward compatibility
export type { TransactionQueryParams, Transaction, TransactionListResponse, FieldPreset };
export { FIELD_PRESETS };

class JuniperQueryApi {
  private readonly baseSource = '/juniper-query';

  async getTransactions(params: TransactionQueryParams = {}): Promise<TransactionListResponse> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `${this.baseSource}/transactions${queryParams.toString() ? `?${queryParams}` : ''}`;
    return api.get<TransactionListResponse>(endpoint);
  }

  async getTransaction(juniperId: string, params: Omit<TransactionQueryParams, 'limit' | 'cursor'> = {}): Promise<Transaction> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `${this.baseSource}/transactions/${juniperId}${queryParams.toString() ? `?${queryParams}` : ''}`;
    return api.get<Transaction>(endpoint);
  }

  async healthCheck(): Promise<{ status: string; tableName: string }> {
    return api.get<{ status: string; tableName: string }>(`${this.baseSource}/health`);
  }

  async getDebugConfig(): Promise<any> {
    return api.get(`${this.baseSource}/debug/config`);
  }

  async simulateWebhookEvent(params: {
    billNumber: string;
    billType: 'operational' | 'trust';
    eventType: 'created' | 'updated' | 'deleted' | 'voided';
    reason?: string;
    stateOverride?: 'draft' | 'awaiting_approval' | 'awaiting_payment' | 'paid' | 'void';
  }): Promise<{
    success: boolean;
    eventId: string;
    sqsMessageId: string;
    webhookId: number;
    billId: number;
    message: string;
  }> {
    return api.post(`${this.baseSource}/webhooks/simulate`, params);
  }

  async bulkSimulateWebhookEvents(params: {
    juniperIds: string[];
    billType: 'operational' | 'trust';
    eventType: 'update' | 'delete';
    requestId: string;
    reason?: string;
  }): Promise<{
    success: boolean;
    results: Array<{
      juniperId: string;
      billNumber: string;
      billType: 'operational' | 'trust';
      success: boolean;
      eventId?: string;
      sqsMessageId?: string;
      error?: string;
    }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
    requestId: string;
    message: string;
  }> {
    return api.post(`${this.baseSource}/webhooks/bulk-simulate`, params);
  }

  async getUserNames(): Promise<Array<{ id: number, name: string }>> {
    return api.get<Array<{ id: number, name: string }>>(`${this.baseSource}/users/names`);
  }
}

export const juniperQueryApi = new JuniperQueryApi();
